import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Link } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { appConfig } from '../../config/appConfig';
import KannadaTagline from '../ui/KannadaTagline';

interface NavItem {
  label: string;
  href: string;
  isExternal?: boolean;
}

const navItems: NavItem[] = [
  { label: 'Tours', href: '/tours' },
  { label: 'Gallery', href: '/gallery' },
  { label: 'Policy & guidelines', href: appConfig.links.refundPolicy },
  { label: 'Online Payment', href: '/payment' },
  { label: 'Reviews', href: '/reviews' },
  { label: 'About Us', href: '/about' },
];

const Header: React.FC = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  const handleMobileMenuToggle = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const mobileMenuPortal = (
    <AnimatePresence>
      {isMobileMenuOpen && (
        <>
          {/* Mobile Menu Overlay */}
          <motion.div
            className="fixed inset-0 bg-black z-[9998] md:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.7 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            onClick={closeMobileMenu}
          />

          {/* Mobile Navigation */}
          <motion.nav 
            className="fixed top-0 right-0 bottom-0 w-4/5 max-w-sm bg-neutral-white shadow-2xl z-[9999] md:hidden overflow-y-auto"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
          >
            <div className="px-6 py-8">
              {/* Close button inside menu */}
              <div className="flex justify-end mb-8">
                <button
                  onClick={closeMobileMenu}
                  className="p-3 text-neutral-dark-gray hover:text-primary-deep-blue transition-colors rounded-full hover:bg-neutral-light-gray"
                  aria-label="Close mobile menu"
                >
                  <X size={28} />
                </button>
              </div>
              
              <ul className="space-y-4">
                {navItems.map((item) => (
                  <li key={item.href}>
                    <Link
                      to={item.href}
                      className="block px-6 py-4 text-lg text-neutral-dark-gray hover:bg-primary-light-blue hover:text-primary-deep-blue rounded-lg transition-all duration-200 font-semibold"
                      onClick={closeMobileMenu}
                    >
                      {item.label}
                    </Link>
                  </li>
                ))}
              </ul>

              <div className="mt-10 px-6">
                <KannadaTagline imgClassName="h-10 w-auto rounded-sm" />
              </div>
            </div>
          </motion.nav>
        </>
      )}
    </AnimatePresence>
  );

  return (
    <>
      <motion.header 
        className={`navbar ${isScrolled ? 'scrolled' : ''}`}
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="navbar-content">
          {/* Logo */}
          <Link to="/" className="navbar-logo" aria-label="Go to home">
            <img
              src="/images/bg_raise_logo.png"
              alt="Raise"
              className="block h-12 md:h-14 lg:h-16 w-auto"
              loading="eager"
              decoding="async"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex flex-1 ml-4 h-12 md:h-14 lg:h-16">
            <ul className="navbar-menu flex-wrap items-stretch">
              {navItems.map((item) => (
                <li key={item.href}>
                  <Link to={item.href} className="nav-btn">
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          {/* Right Side: tagline + mobile menu */}
          <div className="flex items-center gap-3 ml-2">
            <div className="hidden md:block">
              <KannadaTagline imgClassName="h-8 lg:h-9 w-auto rounded-sm" />
            </div>
            <button
              onClick={handleMobileMenuToggle}
              className="md:hidden p-2 text-neutral-dark-gray hover:text-primary-deep-blue transition-colors"
              aria-label="Toggle mobile menu"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </motion.header>

      {/* Render mobile menu using portal */}
      {typeof document !== 'undefined' && ReactDOM.createPortal(mobileMenuPortal, document.body)}
    </>
  );
};

export default Header;
